import { FC } from 'react';
import Head from 'next/head';
import { Theme, useTheme } from '@mui/material/styles';
import paginationQuery from '../../common/helpers/paginationQuery';
import CmsClient from '../../common/clients/CmsClient';
import { BLOG_PAGINATION_LIMIT } from '../../config';
import parseEntity from '../../common/helpers/parseEntity';
import ErrorPage from '../error/[slug]';
import BlogList from '../../components/StaticPage/sections/Blog/BlogList';
import StaticPageLayout from '../../layouts/StaticPageLayout';
import Header from '../../components/StaticPage/Header';
import Footer from '../../components/StaticPage/Footer';
import { BlogProps } from '../../components/StaticPage/types';
import { BlogListItem } from '../../components/StaticPage/types/Item';
import getConfigWithRequest from '../../common/helpers/getConfigWithRequest';
import { cache, generateKey } from '../../common/StaticCache';
import { getFooterMenu, prepareFooter } from '../../common/helpers/getFooterMenu';
import { PageProps, RESOURCE } from '../../common/types/CmsEntities';

export const getServerSideProps = async ({ req, query }: any): Promise<{
    props: PageProps
}> => {
    const limit = (query && Number(query['limit'])) || BLOG_PAGINATION_LIMIT;
    const { cmsApiUrl } = await getConfigWithRequest(req);
    const slug = limit ? '?limit=' + limit : '';
    const queryParams = `${ paginationQuery(0, limit) }&sort[createdAt]=desc`;
    const cacheKey = generateKey('blog-list', slug);
    const cachedData = await cache.get(cacheKey);

    if (cachedData) {
        return cachedData;
    }

    const [
        { data: menu },
        { data: page },
        { data: posts, meta },
        { data: footer },
        { data: footerMenu },
    ] =
        await Promise.all([
            CmsClient.getEntities(req, RESOURCE.menu),
            CmsClient.getEntities(req, RESOURCE.blogList),
            CmsClient.getEntities(
                req,
                RESOURCE.blogPost,
                queryParams,
            ),
            CmsClient.getEntities(req, RESOURCE.footer),
            getFooterMenu({ cmsApiUrl }),
        ]);
    const { total } = meta.pagination;

    const parsedFooter = parseEntity(footer);

    const result = {
        props: {
            menu: parseEntity(menu),
            page: parseEntity(page),
            posts: posts,
            pagination: {
                limit: limit > total ? total : limit,
                hasMore: Boolean(limit < total),
            },
            footer: prepareFooter({
                footer: parsedFooter,
                menu: footerMenu,
            }),
        },
    };

    await cache.set(cacheKey, result);

    return result;
};

const Blog: FC<BlogProps> = ({
    menu,
    page,
    posts,
    pagination,
    footer,
}) => {
    const theme = useTheme<Theme>();

    if (!page) {
        return <ErrorPage code="404" />;
    }

    const { header, name:title, description, keywords } = page;
    const menuItems = menu.body;
    const footerData = footer.content;

    const postsPreviewList = posts.map(p => ({
        id: p.id,
        slug: p.slug,
        image: p.image,
        title: p.title,
        text: p.previewText,
    }) as BlogListItem);

    const seoTitle = title|| parseEntity(header)?.title || 'Our Blog';

    return <>
    {/*
        title, meta or any other elements (e.g. script) need to be contained as
        direct children of the Head element, or wrapped into maximum one level
        of <React.Fragment> or arrays—otherwise the tags won't be correctly
        picked up on client-side navigations.
        https://nextjs.org/docs/pages/api-reference/components/head#use-minimal-nesting
    */}
    <Head>
        <title>{ title }</title>
        { description && <meta name="description" content={ description } /> }
        { keywords && <meta name="keywords" content={ keywords } /> }
    </Head>
    <StaticPageLayout
        title={ seoTitle }
        withActions={ false }
        headerBackground={ theme.palette.primary.main }
        headerBottomSticky={{
            element: <Header data={ header } />,
            zIndex: 10,
            top: 0,
            disableSticky: true,
        }}
        menuItems={ menuItems }
        footer={ <Footer data={ footerData } /> }
    >
        <BlogList posts={ postsPreviewList } pagination={ pagination } />
    </StaticPageLayout>
</>; };

export default Blog;
